import { useSectionAccess } from "../../auth/useSectionAccess";
import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { adminApi, type CompanyContact, type User } from "../../api/admin";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";

/**
 * "Kontakti" card on the company detail page: the company's people. Two groups
 * in one list — portal MEMBERS (the company's app_user accounts, shown with a
 * "portal access" badge; read-only here, managed under Users) and ADDITIONAL
 * CONTACTS (correspondence-only people with no portal account; full CRUD via
 * /admin/companies/{id}/contacts). A converted lead's contact person arrives
 * here automatically as the company's first (primary) contact.
 *
 * Members are derived client-side from the admin users list (User.companyId);
 * callers without users:view simply see the contacts alone — the card never
 * errors out because the member sublist is unavailable.
 */
export function CompanyContactsCard({ companyId, canEdit }: { companyId: string; canEdit: boolean }) {
  const { canFull } = useSectionAccess("companies");
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [contacts, setContacts] = useState<CompanyContact[] | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [editing, setEditing] = useState<CompanyContact | null>(null);
  const [creating, setCreating] = useState(false);

  async function loadContacts() {
    try {
      setContacts(await adminApi.listCompanyContacts(companyId));
    } catch {
      setContacts([]);
    }
  }

  useEffect(() => {
    void loadContacts();
    // Members are a nice-to-have: swallow errors (e.g. caller lacks users:view).
    void adminApi
      .listUsers(0, 200)
      .then((p) => setMembers(p.content.filter(Boolean).filter(
          (u) => u.companyId === companyId && u.status === "ACTIVE")))
      .catch(() => setMembers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function handleDelete(c: CompanyContact) {
    const ok = await confirm({
      title: t("admin.contacts.deleteTitle", "Delete contact?"),
      message: c.name,
      danger: true,
    });
    if (!ok) return;
    try {
      await adminApi.deleteCompanyContact(companyId, c.id);
      toast.success(t("admin.contacts.deleted", "Contact deleted."));
      void loadContacts();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <section className="card">
      <header className="card-h" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3>{t("admin.contacts.title", "Contacts")}</h3>
        {canEdit && (
          <button type="button" className="btn primary sm" onClick={() => setCreating(true)}>
            + {t("admin.contacts.add", "Add contact")}
          </button>
        )}
      </header>
      <div className="card-b">
        {members.length === 0 && (contacts?.length ?? 0) === 0 ? (
          <div className="empty">{t("admin.contacts.empty", "No contacts recorded for this company yet.")}</div>
        ) : (
          <table className="list-card">
            <thead>
              <tr>
                <th>{t("admin.contacts.col.name", "Name")}</th>
                <th>{t("admin.contacts.col.position", "Position")}</th>
                <th>{t("admin.contacts.col.email", "Email")}</th>
                <th>{t("admin.contacts.col.phone", "Phone")}</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={`u-${m.id}`}>
                  <td data-label={t("admin.contacts.col.name", "Name")}>
                    <b>{`${m.firstName} ${m.lastName}`.trim() || m.email}</b>{" "}
                    <span style={portalBadge}>{t("admin.contacts.portalAccess", "portal access")}</span>
                  </td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.position", "Position")}>—</td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.email", "Email")}>{m.email}</td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.phone", "Phone")}>{m.phone ?? "—"}</td>
                  <td />
                </tr>
              ))}
              {(contacts ?? []).map((c) => (
                <tr key={c.id}>
                  <td data-label={t("admin.contacts.col.name", "Name")}>
                    <b>{c.name}</b>
                    {c.primary && <span style={primaryBadge}>{t("admin.contacts.primary", "primary")}</span>}
                  </td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.position", "Position")}>{c.position ?? "—"}</td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.email", "Email")}>{c.email ?? "—"}</td>
                  <td style={mutedTd} data-label={t("admin.contacts.col.phone", "Phone")}>{c.phone ?? "—"}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }} className="card-actions">
                    {canEdit && (
                      <>
                        <button type="button" className="btn sm" onClick={() => setEditing(c)}>
                          {t("action.edit", "Edit")}
                        </button>{" "}
                        <button type="button" className="btn sm danger" disabled={!canFull} onClick={() => void handleDelete(c)}>
                          {t("action.delete", "Delete")}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {canEdit && (creating || editing) && (
        <ContactFormModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={async (body) => {
            if (editing) await adminApi.updateCompanyContact(companyId, editing.id, body);
            else await adminApi.createCompanyContact(companyId, body);
            toast.success(editing
              ? t("admin.contacts.updated", "Contact updated.")
              : t("admin.contacts.created", "Contact added."));
            setCreating(false);
            setEditing(null);
            void loadContacts();
          }}
        />
      )}
    </section>
  );
}

function ContactFormModal({
  initial, onClose, onSubmit,
}: {
  initial: CompanyContact | null;
  onClose: () => void;
  onSubmit: (body: { name: string; position?: string | null; email?: string | null; phone?: string | null; primary?: boolean }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [position, setPosition] = useState(initial?.position ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [primary, setPrimary] = useState(initial?.primary ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        position: position.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        primary,
      });
    } catch (err) {
      setError((err as ApiError).message);
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onClick={onClose} role="dialog" aria-modal="true"
         aria-label={initial ? t("admin.contacts.editTitle", "Edit contact") : t("admin.contacts.newTitle", "New contact")}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 14px", fontSize: 17, color: "var(--ink)" }}>
          {initial ? t("admin.contacts.editTitle", "Edit contact") : t("admin.contacts.newTitle", "New contact")}
        </h3>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={fieldStyle}>
            <span style={labelStyle}>{t("admin.contacts.field.name", "Name")} <span style={{ color: "var(--err)" }}>*</span></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus required maxLength={200} />
          </label>
          <label style={fieldStyle}>
            <span style={labelStyle}>{t("admin.contacts.field.position", "Position")}</span>
            <input className="input" value={position} onChange={(e) => setPosition(e.target.value)} maxLength={150} />
          </label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <label style={{ ...fieldStyle, flex: 1, minWidth: 170 }}>
              <span style={labelStyle}>{t("admin.contacts.field.email", "Email")}</span>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} />
            </label>
            <label style={{ ...fieldStyle, flex: 1, minWidth: 150 }}>
              <span style={labelStyle}>{t("admin.contacts.field.phone", "Phone")}</span>
              <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={60} />
            </label>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink-2)" }}>
            <input type="checkbox" checked={primary} onChange={(e) => setPrimary(e.target.checked)} />
            {t("admin.contacts.field.primary", "Primary contact")}
          </label>
          {error && <div style={{ color: "var(--err)", fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              {t("action.cancel", "Cancel")}
            </button>
            <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
              {busy ? t("action.saving", "Saving…") : t("action.save", "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const portalBadge: CSSProperties = {
  marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "var(--on-primary)",
  background: "var(--brand-primary)", padding: "1px 7px", borderRadius: 999, verticalAlign: "middle",
};
const primaryBadge: CSSProperties = {
  marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: "var(--ink-2)",
  background: "var(--surface-2)", border: "1px solid var(--border)", padding: "1px 7px",
  borderRadius: 999, verticalAlign: "middle",
};
const mutedTd: CSSProperties = { color: "var(--ink-2)", fontSize: 13 };
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "grid", placeItems: "center", padding: 20, zIndex: 60,
};
const panel: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)",
  boxShadow: "var(--shadow-2)", padding: 22, width: 480, maxWidth: "100%",
};
const fieldStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelStyle: CSSProperties = { fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 };
